import Button from '@/components/Button'
import { useAuth } from '@/context/auth'
import { useModal } from '@/context/modal'
import { cloudflareImageLoader } from '@/lib/cloudflareImageLoader'
import { getConfig } from '@/lib/config'
import { addToStringArrayCookie, hasInStringArrayCookie } from '@/lib/cookie'
import { Banner } from '@/shared/api-generated-types'
import Modal from '@/widgets/Modal'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { gql, useQuery } from 'urql'

const QUERY_ACTIVE_BANNER = gql`
  query Banner {
    banners {
      __typename
      id
      title
      action
      active
      asset {
        asset {
          type
          key
          bucketName
          bucketRegion
        }
        cdnUrl
      }
      delay
      description
      label
      createdAt
      updatedAt
    }
  }
`

const cookieName = 'closedBanners'

const { publicRuntimeConfig } = getConfig()

export const PromoBanner = (): JSX.Element | null => {
  const [{ data, fetching }] = useQuery({
    query: QUERY_ACTIVE_BANNER,
    variables: {},
    requestPolicy: 'cache-first',
  })
  const {
    state: { isOpen },
    dispatch,
  } = useModal()

  const [banner, setBanner] = useState<Banner | null>(null)
  const {
    state: { user },
  } = useAuth()

  const handleButtonClick = (link: string): void => {
    window.location.href = link

    if (banner?.id) addToStringArrayCookie(cookieName, banner.id)
  }

  const handleClose = (): void => {
    if (banner?.id) addToStringArrayCookie(cookieName, banner.id)
    dispatch({ type: 'TOGGLE' })
  }

  useEffect(() => {
    if (fetching) return
    setBanner(data.banners)
  }, [data, fetching])

  useEffect(() => {
    if (!user?.isVerified || !banner) return

    if (!hasInStringArrayCookie(cookieName, banner.id)) {
      const timer = setTimeout(() => {
        dispatch({ type: 'TOGGLE' })
      }, banner.delay || publicRuntimeConfig.dialogs.auth.delay)

      return () => clearTimeout(timer)
    }
  }, [banner, user])

  return !fetching && banner && isOpen ? (
    <Modal className="p-0" isOpen={isOpen} onClose={handleClose} onDismiss={handleClose}>
      {banner.asset && banner.asset.cdnUrl && (
        <Image
          src={banner.asset.cdnUrl}
          height={300}
          width={500}
          className="relative w-full h-[200px] xxl:h-[300px] object-cover [border-top-left-radius:inherit] [border-top-right-radius:inherit]"
          loader={cloudflareImageLoader}
          alt="banner"
        />
      )}
      <div className="p-3">
        {banner.title && <h1 className="font-bold text-center m-2 text-lg xxl:text-2xl">{banner.title}</h1>}
        {banner.description && <p className="px-5 text-base xxl:text-lg xxl:m-2">{banner.description}</p>}
      </div>
      {banner.action && (
        <Button
          label={banner.label || 'Lets Go!'}
          onClick={() => handleButtonClick(banner.action!)}
          component="button"
          className="w-full mb-5"
          theme="secondary"
        />
      )}
    </Modal>
  ) : null
}
